// Connects the FAMU vs Jackson State Game of the Week article (iart_2cebae571d1d) to community polls + predictions.
// Runs after the article's own script and takes over its device-only poll / prediction handlers.
import axios from 'axios';
import { getVisitorId } from '../lib/visitorId';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/community`;
const ARTICLE_URL = 'https://shelovesthee.com/interactive/iart_2cebae571d1d';
const POLL_IDS = [
  'famu-jsu-2026-edge-offense',
  'famu-jsu-2026-edge-defense',
  'famu-jsu-2026-edge-special-teams',
  'famu-jsu-2026-edge-atmosphere'
];
const OPTIONS = ['FAMU', 'Jackson State', 'Even'];
const GAME_ID = 'famu-jsu-2026-10-17';
const LEGACY_POLL_KEY = (i) => `famu-jsu-2026-edge-${i}`;
const LEGACY_PRED_KEY = 'shelovesthee-famu-jsu-2026-10-17';
const DISMISS_KEY = 'slt-legacy-dismissed-famu-jsu-2026';
const REFRESH_MS = 30000;
const TEAM_NAMES = { famu: 'FAMU', jsu: 'Jackson State' };

const STYLES = `
#scoreForm input{color:#0d1726;background:#fff}
#scoreForm button{background:#d71920;color:#fff}
#scoreForm button:disabled{opacity:.7;cursor:wait}
.slt-cp-results{margin-top:12px;text-align:left}
.slt-cp-row{margin:8px 0;font-size:14px}
.slt-cp-row-head{display:flex;justify-content:space-between;gap:8px;font-weight:600;color:#0d1726}
.slt-cp-bar{height:10px;background:#e6ecf3;border-radius:999px;overflow:hidden;margin-top:4px}
.slt-cp-fill{height:100%;background:#0b315f;border-radius:999px;transition:width .45s ease}
.slt-cp-mine .slt-cp-fill{background:#d71920}
.slt-cp-tag{display:inline-block;background:#d71920;color:#fff;border-radius:999px;padding:1px 8px;font-size:11px;margin-left:6px;vertical-align:middle}
.slt-cp-meta{font-size:12px;color:#64748b;margin-top:6px}
.slt-cp-empty{font-size:13px;color:#64748b;font-style:italic}
.slt-cp-err{color:#b91c1c;font-weight:700}
.slt-cp-ok{color:#15803d;font-weight:700}
.slt-cp-btn{display:inline-block;margin:6px 6px 0 0;padding:6px 14px;border-radius:999px;border:2px solid #0b315f;background:#0b315f;color:#fff;font-weight:700;font-size:13px;cursor:pointer}
.slt-cp-btn.secondary{background:#fff;color:#0b315f}
.slt-cp-btn:disabled{opacity:.6;cursor:wait}
.slt-cp-legacy{border:2px dashed #d71920;border-radius:16px;padding:14px 16px;margin:0 0 16px;background:#fff7f7;color:#0d1726;font-size:14px}
.slt-cp-pred{margin-top:18px;background:#ffffff12;border:1px solid #ffffff33;border-radius:16px;padding:16px;text-align:left}
.slt-cp-pred h3{margin:0 0 8px;font-size:18px}
.slt-cp-split{display:flex;height:16px;border-radius:999px;overflow:hidden;background:#ffffff22;margin:8px 0}
.slt-cp-split div{height:100%;transition:width .45s ease}
.slt-cp-legend{display:flex;flex-wrap:wrap;gap:12px;font-size:13px}
.slt-cp-dot{display:inline-block;width:10px;height:10px;border-radius:50%;margin-right:4px}
.slt-cp-avg{margin-top:12px}
.slt-cp-avg .slt-cp-bar{background:#ffffff22;height:14px}
.slt-cp-pred .slt-cp-meta,.slt-cp-pred .slt-cp-empty{color:#cbd5e1}
.slt-cp-pred .slt-cp-row-head{color:inherit}
`;
const COLORS = { famu: '#d71920', jsu: '#54b5e6', tie: '#94a3b8' };

const el = (tag, attrs = {}, text) => {
  const e = document.createElement(tag);
  Object.entries(attrs).forEach(([k, v]) => (k === 'class' ? (e.className = v) : e.setAttribute(k, v)));
  if (text !== undefined) e.textContent = text;
  return e;
};

const readLegacy = (key) => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const writeLegacy = (key, value) => {
  try { localStorage.setItem(key, value); } catch { /* storage unavailable */ }
};
const validScore = (n) => Number.isInteger(n) && n >= 0 && n <= 99;
const errorText = (e, fallback) => e?.response?.data?.detail && typeof e.response.data.detail === 'string' ? e.response.data.detail : fallback;

export default function enhanceFamuJsu(container) {
  const pollEls = Array.from(container.querySelectorAll('.poll'));
  const form = container.querySelector('#scoreForm');
  if (pollEls.length !== POLL_IDS.length && !form) return () => {};

  const visitorId = getVisitorId();
  let cancelled = false;
  const style = el('style', {}, STYLES);
  container.prepend(style);
  window.SHELOVESTHEE_PREDICTION_SHARE_URL = ARTICLE_URL;

  // ---------- Polls ----------
  const polls = pollEls.slice(0, POLL_IDS.length).map((p, i) => {
    const results = el('div', { class: 'slt-cp-results', 'data-testid': `community-poll-results-${i}` });
    p.querySelector('.poll-result')?.after(results);
    return { root: p, i, id: POLL_IDS[i], results, statusEl: p.querySelector('.poll-result'), state: null, pending: null, saving: false, error: null, message: '', legacy: null };
  });

  const renderPoll = (poll) => {
    const { root, state, pending, saving, error, results, statusEl } = poll;
    const shown = pending || state?.myVote || null;
    root.querySelectorAll('.poll-options button').forEach((b) => {
      b.type = 'button';
      b.disabled = saving;
      b.classList.toggle('selected', b.dataset.pick === shown);
      b.setAttribute('aria-pressed', String(b.dataset.pick === shown));
    });
    if (statusEl) {
      statusEl.className = 'poll-result';
      if (!state && !error) statusEl.textContent = 'Loading community results…';
      else if (saving) statusEl.textContent = `Saving your vote for ${pending}…`;
      else if (error) { statusEl.textContent = error; statusEl.classList.add('slt-cp-err'); }
      else if (poll.message) { statusEl.textContent = poll.message; statusEl.classList.add('slt-cp-ok'); }
      else if (state?.myVote) statusEl.textContent = `Your edge: ${state.myVote}`;
      else if (poll.legacy) statusEl.textContent = `Saved on this device only: ${poll.legacy} — not counted yet`;
      else statusEl.textContent = '';
      statusEl.setAttribute('data-testid', `community-poll-status-${poll.i}`);
    }
    results.replaceChildren();
    if (error && pending) {
      const retry = el('button', { class: 'slt-cp-btn', type: 'button', 'data-testid': `community-poll-retry-${poll.i}` }, 'Retry');
      retry.onclick = () => vote(poll, pending);
      results.append(retry);
    }
    if (!state) return;
    if (state.totalVoters === 0) {
      results.append(el('p', { class: 'slt-cp-empty', 'data-testid': `community-poll-empty-${poll.i}` }, 'No community votes yet — be the first to weigh in.'));
      return;
    }
    state.options.forEach((o) => {
      const mine = o.value === state.myVote;
      const row = el('div', { class: `slt-cp-row${mine ? ' slt-cp-mine' : ''}` });
      const head = el('div', { class: 'slt-cp-row-head' });
      const label = el('span', {}, o.value);
      if (mine) label.append(el('span', { class: 'slt-cp-tag' }, 'Your vote'));
      head.append(label, el('span', {}, `${o.percent}% · ${o.count} ${o.count === 1 ? 'vote' : 'votes'}`));
      const bar = el('div', { class: 'slt-cp-bar' });
      const fill = el('div', { class: 'slt-cp-fill' });
      fill.style.width = `${o.percent}%`;
      bar.append(fill);
      row.append(head, bar);
      results.append(row);
    });
    results.append(el('p', { class: 'slt-cp-meta', 'data-testid': `community-poll-total-${poll.i}` }, `${state.totalVoters} total ${state.totalVoters === 1 ? 'voter' : 'voters'}`));
  };

  const vote = async (poll, choice) => {
    if (poll.saving) return;
    Object.assign(poll, { pending: choice, saving: true, error: null, message: '' });
    renderPoll(poll);
    try {
      const res = await axios.post(`${API}/polls/vote`, { pollId: poll.id, choice, visitorId }, { withCredentials: true });
      if (cancelled) return;
      Object.assign(poll, { state: res.data.poll, pending: null, saving: false, legacy: null, message: `Vote recorded — Your edge: ${res.data.poll.myVote}` });
      writeLegacy(LEGACY_POLL_KEY(poll.i), res.data.poll.myVote);
    } catch (e) {
      if (cancelled) return;
      Object.assign(poll, { saving: false, error: `${errorText(e, "Couldn't save your vote.")} Your pick (${choice}) hasn't been counted yet.` });
    }
    renderPoll(poll);
    renderLegacyBanner();
  };

  polls.forEach((poll) => {
    poll.root.querySelectorAll('.poll-options button').forEach((b) => { b.onclick = () => vote(poll, b.dataset.pick); });
    renderPoll(poll);
  });

  const loadPolls = async () => {
    try {
      const res = await axios.get(`${API}/polls/state`, { params: { pollIds: POLL_IDS.join(','), visitorId }, withCredentials: true });
      if (cancelled) return;
      polls.forEach((poll) => {
        poll.state = res.data.polls[poll.id];
        if (!poll.saving && !poll.error) poll.pending = null;
        if (poll.error && !poll.pending) poll.error = null;
        renderPoll(poll);
      });
      return true;
    } catch (e) {
      if (cancelled) return;
      polls.forEach((poll) => {
        if (!poll.state) { poll.error = 'Community results are unavailable right now.'; renderPoll(poll); }
      });
      return false;
    }
  };

  // ---------- Legacy device-only selections ----------
  const pollSection = pollEls[0]?.closest('section');
  const legacyBanner = el('div', { class: 'slt-cp-legacy', 'data-testid': 'legacy-polls-banner' });
  legacyBanner.hidden = true;
  pollSection?.querySelector('.poll-grid')?.before(legacyBanner);

  const renderLegacyBanner = () => {
    const pendingLegacy = polls.filter((p) => p.legacy && !p.state?.myVote);
    if (!pendingLegacy.length || readLegacy(DISMISS_KEY)) { legacyBanner.hidden = true; return; }
    legacyBanner.hidden = false;
    legacyBanner.replaceChildren(
      el('strong', {}, 'We found matchup-edge picks saved on this device. '),
      el('span', {}, `They haven't been counted in the community results: ${pendingLegacy.map((p) => `${p.root.querySelector('h3')?.textContent}: ${p.legacy}`).join(' • ')}.`),
      el('br')
    );
    const submit = el('button', { class: 'slt-cp-btn', type: 'button', 'data-testid': 'legacy-polls-submit' }, 'Submit these picks');
    const dismiss = el('button', { class: 'slt-cp-btn secondary', type: 'button', 'data-testid': 'legacy-polls-dismiss' }, 'No thanks');
    submit.onclick = async () => {
      submit.disabled = dismiss.disabled = true;
      submit.textContent = 'Submitting…';
      for (const p of pendingLegacy) await vote(p, p.legacy);
    };
    dismiss.onclick = () => {
      writeLegacy(DISMISS_KEY, '1');
      polls.forEach((p) => { p.legacy = null; renderPoll(p); });
      renderLegacyBanner();
    };
    legacyBanner.append(submit, dismiss);
  };

  // ---------- Predictions ----------
  const famuInput = container.querySelector('#famuScore');
  const jsuInput = container.querySelector('#jsuScore');
  const scoreMsg = container.querySelector('#scoreMsg');
  const storageNote = container.querySelector('#storageNote');
  const shareBox = container.querySelector('#predictionShare');
  const submitBtn = form?.querySelector('button');
  const graphicStatus = container.querySelector('#graphicStatus');
  const predBox = el('div', { class: 'slt-cp-pred', 'data-testid': 'community-predictions' });
  storageNote?.after(predBox);
  storageNote?.setAttribute('data-testid', 'prediction-status');
  let confirmed = null;
  let predState = null;
  let predSaving = false;

  const setNote = (text, cls = '', actions = []) => {
    if (!storageNote) return;
    storageNote.className = cls;
    storageNote.replaceChildren(document.createTextNode(text), ...actions);
  };

  const showGraphic = (p) => {
    if (typeof window.show === 'function') {
      try { window.show(p); } catch { shareBox && (shareBox.hidden = false); }
    } else if (shareBox) {
      shareBox.hidden = false;
    }
  };

  const renderPredictions = () => {
    predBox.replaceChildren(el('h3', {}, 'Community predictions'));
    if (!predState) { predBox.append(el('p', { class: 'slt-cp-meta' }, 'Loading community predictions…')); return; }
    const { totalPredictions: total, picks, averages } = predState;
    if (!total) { predBox.append(el('p', { class: 'slt-cp-empty', 'data-testid': 'predictions-empty' }, 'No community predictions yet — be the first to make the call.')); return; }
    predBox.append(el('p', { class: 'slt-cp-meta', 'data-testid': 'predictions-total' }, `${total} ${total === 1 ? 'prediction' : 'predictions'} submitted`));
    const split = el('div', { class: 'slt-cp-split', role: 'img', 'aria-label': `FAMU ${picks.famu.percent}%, tie ${picks.tie.percent}%, Jackson State ${picks.jsu.percent}%` });
    const legend = el('div', { class: 'slt-cp-legend' });
    [['famu', 'FAMU wins'], ['tie', 'Tie'], ['jsu', 'Jackson State wins']].forEach(([k, label]) => {
      const seg = el('div');
      seg.style.width = `${picks[k].percent}%`;
      seg.style.background = COLORS[k];
      split.append(seg);
      const item = el('span', { 'data-testid': `predictions-pick-${k}` });
      const dot = el('span', { class: 'slt-cp-dot' });
      dot.style.background = COLORS[k];
      item.append(dot, document.createTextNode(`${label}: ${picks[k].percent}% (${picks[k].count})`));
      legend.append(item);
    });
    predBox.append(split, legend);
    const avg = el('div', { class: 'slt-cp-avg', 'data-testid': 'predictions-averages' });
    avg.append(el('p', { class: 'slt-cp-meta' }, 'Average predicted score'));
    const max = Math.max(averages.famu || 0, averages.jsu || 0, 1);
    ['famu', 'jsu'].forEach((t) => {
      const row = el('div', { class: 'slt-cp-row' });
      const head = el('div', { class: 'slt-cp-row-head' });
      head.append(el('span', {}, TEAM_NAMES[t]), el('span', {}, averages[t].toFixed(1)));
      const bar = el('div', { class: 'slt-cp-bar' });
      const fill = el('div', { class: 'slt-cp-fill' });
      fill.style.width = `${(averages[t] / max) * 100}%`;
      fill.style.background = COLORS[t];
      bar.append(fill);
      row.append(head, bar);
      avg.append(row);
    });
    predBox.append(avg);
  };

  const submitPrediction = async (famu, jsu) => {
    if (predSaving) return;
    predSaving = true;
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Saving…'; }
    setNote('Submitting your prediction to the community…');
    try {
      const res = await axios.post(`${API}/predictions`, { gameId: GAME_ID, visitorId, scores: { famu, jsu } }, { withCredentials: true });
      if (cancelled) return;
      predState = res.data.state;
      confirmed = { gameId: GAME_ID, famu: predState.myPrediction.famu, jsu: predState.myPrediction.jsu };
      writeLegacy(LEGACY_PRED_KEY, JSON.stringify({ ...confirmed, submittedAt: predState.myPrediction.updatedAt }));
      showGraphic(confirmed);
      setNote('Prediction submitted to the community. Share your pick below — or update it anytime.', 'slt-cp-ok');
    } catch (e) {
      if (cancelled) return;
      const retry = el('button', { class: 'slt-cp-btn', type: 'button', 'data-testid': 'prediction-retry' }, 'Retry');
      retry.onclick = () => submitPrediction(famu, jsu);
      setNote(`${errorText(e, "Couldn't submit your prediction.")} Your scores are still entered — `, 'slt-cp-err', [retry]);
    } finally {
      predSaving = false;
      if (submitBtn && !cancelled) { submitBtn.disabled = false; submitBtn.textContent = confirmed ? 'Update my prediction' : 'Save my prediction'; }
      renderPredictions();
    }
  };

  if (form) {
    form.onsubmit = (e) => {
      e.preventDefault();
      if (!form.reportValidity()) return;
      const famu = Number(famuInput.value);
      const jsu = Number(jsuInput.value);
      if (!validScore(famu) || !validScore(jsu)) { setNote('Scores must be whole numbers from 0 to 99.', 'slt-cp-err'); return; }
      submitPrediction(famu, jsu);
    };
    if (submitBtn) submitBtn.setAttribute('data-testid', 'prediction-submit');
    if (shareBox) shareBox.hidden = true;
    if (scoreMsg) scoreMsg.textContent = '';
    setNote('Loading your prediction…');
  }

  const predictionText = (p) => (typeof window.predictionText === 'function'
    ? window.predictionText(p)
    : `I predicted on SheLovesThee.com! FAMU ${p.famu} — Jackson State ${p.jsu}. Who you got? #FAMUvsJSU #HBCUFootball #SheLovesThee`);
  const graphicBlob = () => new Promise((resolve, reject) => {
    const canvas = container.querySelector('#predictionCanvas');
    canvas ? canvas.toBlob((b) => (b ? resolve(b) : reject(Error('no image'))), 'image/png') : reject(Error('no canvas'));
  });
  const saveBtn = container.querySelector('#savePredictionGraphic');
  const shareBtn = container.querySelector('#sharePredictionGraphic');
  if (saveBtn) {
    saveBtn.onclick = async () => {
      if (!confirmed) return;
      try {
        const url = URL.createObjectURL(await graphicBlob());
        const a = document.createElement('a');
        a.href = url;
        a.download = `SheLovesThee-FAMU-${confirmed.famu}-JSU-${confirmed.jsu}-prediction.png`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 30000);
        if (graphicStatus) graphicStatus.textContent = 'Graphic ready to save. Attach it to your X post.';
      } catch {
        if (graphicStatus) graphicStatus.textContent = 'Could not save the graphic. Please try again.';
      }
    };
  }
  if (shareBtn) {
    shareBtn.onclick = async () => {
      if (!confirmed) return;
      try {
        const file = new File([await graphicBlob()], 'SheLovesThee-FAMU-JSU-prediction.png', { type: 'image/png' });
        if (!navigator.canShare?.({ files: [file] })) {
          if (graphicStatus) graphicStatus.textContent = 'Image sharing is unavailable here. Use Save graphic and Post to X.';
          return;
        }
        await navigator.share({ files: [file], title: 'My SheLovesThee prediction', text: `${predictionText(confirmed)} ${ARTICLE_URL}#predictions` });
      } catch (e) {
        if (e.name !== 'AbortError' && graphicStatus) graphicStatus.textContent = 'Use Save graphic and Post to X to share your prediction.';
      }
    };
  }

  const loadPredictions = async (initial) => {
    try {
      const res = await axios.get(`${API}/predictions/state`, { params: { gameId: GAME_ID, visitorId }, withCredentials: true });
      if (cancelled) return;
      predState = res.data;
      const mine = predState.myPrediction;
      if (initial && form) {
        if (mine) {
          confirmed = { gameId: GAME_ID, famu: mine.famu, jsu: mine.jsu };
          famuInput.value = mine.famu;
          jsuInput.value = mine.jsu;
          showGraphic(confirmed);
          if (submitBtn) submitBtn.textContent = 'Update my prediction';
          setNote('Your prediction is saved in the community totals. You can update it anytime.', 'slt-cp-ok');
        } else {
          let legacy = null;
          try { legacy = JSON.parse(readLegacy(LEGACY_PRED_KEY)); } catch { legacy = null; }
          if (legacy && validScore(legacy.famu) && validScore(legacy.jsu) && !readLegacy(`${DISMISS_KEY}-prediction`)) {
            famuInput.value = legacy.famu;
            jsuInput.value = legacy.jsu;
            const yes = el('button', { class: 'slt-cp-btn', type: 'button', 'data-testid': 'legacy-prediction-submit' }, 'Submit it');
            const no = el('button', { class: 'slt-cp-btn secondary', type: 'button', 'data-testid': 'legacy-prediction-dismiss' }, 'No thanks');
            yes.onclick = () => submitPrediction(legacy.famu, legacy.jsu);
            no.onclick = () => { writeLegacy(`${DISMISS_KEY}-prediction`, '1'); setNote('Enter your scores and save to join the community predictions.'); };
            setNote(`We found a prediction saved on this device (FAMU ${legacy.famu} — Jackson State ${legacy.jsu}). It hasn't been submitted to the community yet. `, '', [yes, no]);
          } else {
            setNote('Enter your scores and save to join the community predictions.');
          }
        }
      }
    } catch (e) {
      if (cancelled) return;
      if (initial) {
        const retry = el('button', { class: 'slt-cp-btn', type: 'button', 'data-testid': 'prediction-load-retry' }, 'Retry');
        retry.onclick = () => { setNote('Loading your prediction…'); loadPredictions(true); };
        setNote('Community predictions are unavailable right now. ', 'slt-cp-err', [retry]);
      }
    }
    renderPredictions();
  };

  // ---------- Boot + periodic refresh ----------
  (async () => {
    if (polls.length) {
      await loadPolls();
      if (cancelled) return;
      polls.forEach((p) => {
        const saved = readLegacy(LEGACY_POLL_KEY(p.i));
        if (p.state && !p.state.myVote && OPTIONS.includes(saved)) { p.legacy = saved; renderPoll(p); }
      });
      renderLegacyBanner();
    }
    if (form) await loadPredictions(true);
  })();

  const timer = setInterval(() => {
    if (document.visibilityState !== 'visible') return;
    if (polls.length) loadPolls();
    if (form && !predSaving) loadPredictions(false);
  }, REFRESH_MS);

  return () => {
    cancelled = true;
    clearInterval(timer);
    style.remove();
    if (window.SHELOVESTHEE_PREDICTION_SHARE_URL === ARTICLE_URL) delete window.SHELOVESTHEE_PREDICTION_SHARE_URL;
  };
}
